import {
  dataModelerSetSchemaDataModel,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import {
  ISchema,
  ISchemaDataModelQuery,
  ISchemaDataModelQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import KubitContextualMenu from '../../../../components/Dropdown/ContextualMenu/KubitContextualMenu';
import { DirectionalHint, IContextualMenuItem } from '@fluentui/react';
import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useLazyQuery } from '@apollo/client';
import { DATA_MODEL_GET_SCHEMA } from '../../../../utils/apollo/dataModeler/queries';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import loadedSchemaToCopyDefinitionMenuItems from './loadedSchemaToCopyDefinitionMenuItems';
import { assertNotNil } from '../../../../utils/types';
import { MenuItemTriggerDiv } from './DataModelerDerivedColumnCopyDefinitionUI';

interface IProps {
  forSchema: ISchema;
  updateDraft: (props: Partial<IDerivedColumnDraft>) => void;
  dismissParent: () => void;
}
const DerivedColumnCopySchemaDefinitions = ({
  forSchema,
  updateDraft,
  dismissParent,
}: IProps) => {
  const handleUpdateDraft = useCallback(
    (props: Partial<IDerivedColumnDraft>) => {
      updateDraft(props);
      dismissParent();
    },
    [updateDraft, dismissParent],
  );

  const dispatch = useAppDispatch();
  const schemaState = useAppSelector(
    (rootState) => rootState.dataModeler.schemaStates[forSchema.id],
  );

  const onSchemaLoad = useCallback(
    (data: ISchemaDataModelQuery) => {
      dispatch(
        dataModelerSetSchemaDataModel({
          newModel: data.schemaDataModel,
          setAsCurrent: false,
        }),
      );
    },
    [dispatch],
  );

  const [loadSchema] = useLazyQuery<
    ISchemaDataModelQuery,
    ISchemaDataModelQueryVariables
  >(DATA_MODEL_GET_SCHEMA);

  const schemaObj = schemaState?.schemaDataModel;
  const draftEdits = schemaState?.draftEdits ?? defaultEmptyDraftEdits;

  const [currentItems, setCurrentItems] = useState<IContextualMenuItem[]>(
    !!schemaObj
      ? loadedSchemaToCopyDefinitionMenuItems(
          schemaObj,
          draftEdits,
          handleUpdateDraft,
        )
      : [
          {
            key: `${forSchema}-loading`,
            text: 'Loading ...',
          },
        ],
  );

  const onMenuOpened = useCallback(async () => {
    if (!!schemaObj) {
      return;
    }
    loadSchema({
      variables: {
        schemaKey: forSchema.id,
      },
    })
      .then((resp) => {
        assertNotNil(resp.data);
        setCurrentItems(
          loadedSchemaToCopyDefinitionMenuItems(
            resp.data.schemaDataModel,
            draftEdits,
            handleUpdateDraft,
          ),
        );
        // also cache the data locally
        onSchemaLoad(resp.data);
      })
      .catch(() => {
        setCurrentItems([
          {
            key: `${forSchema}-error`,
            text: 'Error loading',
          },
        ]);
      });
  }, [
    draftEdits,
    forSchema,
    handleUpdateDraft,
    loadSchema,
    onSchemaLoad,
    schemaObj,
  ]);

  return (
    <KubitContextualMenu
      items={currentItems}
      onMenuOpened={() => onMenuOpened()}
      trigger={
        <MenuItemTriggerDiv>
          <a>{forSchema.name}</a>
        </MenuItemTriggerDiv>
      }
      directionalHint={DirectionalHint.rightCenter}
    />
  );
};

export default DerivedColumnCopySchemaDefinitions;
